<template>
    <div class="small">
        <div class="actions position-absolute d-flex flex-row align-content-end w-100">
            <button
                @click="remove(quote.shortListID, quote.quoteID), $parent.prev(type)"
                class="button button--remove">
                <i class="icon-cross"></i><span>Remove</span>
            </button>

            <template v-if="expanded">
                <input type="checkbox" :id="`expanded-${quote.derivativeID}`" :name="`expanded-${quote.derivativeID}`"
                       :value="listIndex" v-model="expandedStatus">
                <label :for="`expanded-${quote.derivativeID}`"></label>
            </template>

        </div>
        <div class="row">
            <div class="col-3">
                <div class="badge vehicle-badge">
                    <!-- Plugin Hybrid -->
                    <i v-if="fuelTypeElectricOrPluginHybrid(quote.derivative.fuelTypeDescription)" class="icon-plug-in" />

                    <!-- Electric -->
                    <i v-if="fuelTypeElectric(quote.derivative.fuelTypeDescription)" class="icon-electric" />
                </div>
                <vehicle-image :derivative="quote" :showMain="true"></vehicle-image>
            </div>
            <div class="col-9">
                <div class="small-title">
                    <h3 class="make"><span>{{quote.manufacturerName}}</span> {{
                            quote.modelName }}</h3>
                    <h4 class="model">{{quote.derivativeName}}</h4>

                    <ul class="full-spec d-flex flex-row">
                        <li class="d-flex flex-row">
                            <span class="key">Quote Ref:</span><span class="value">{{ quote.quote.quoteNumber }}</span>
                        </li>
                        <li class="d-flex flex-row">
                            <span class="key">Your Ref:</span><span class="value">{{ quote.quote.definition.quoteName }}</span>
                        </li>
                    </ul>

                    <div>
                        <ul class="spec d-flex flex-row">
                            <li class="d-flex flex-column">
                                <span class="key">Monthly BIK</span><span class="value">£{{ quote.quote.details.biK1Rate }}</span></li>
                            <li class="d-flex flex-column">
                                <span class="key">Fuel Type</span><span class="value">{{ quote.derivative.fuelTypeDescription }}</span>
                            </li>
                            <li class="d-flex flex-column">
                                <span class="key">P11d Value</span><span class="value">£{{ Number(quote.quote.details.pricing.p11DValue).toFixed(2) }}</span>
                            </li>
                            <li class="d-flex flex-column">
                                <span class="key">Vehicle Term</span><span class="value">{{ Number(quote.term) }} Months</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <div class="section-footer">
            <div class="row">
                <div class="col-12 col-xl-6">
                    <div class="total d-flex justify-content-between">
                        <span class="key">Total Monthly Net Cost (Estimated)</span>
                        <span class="value">£{{ getTotalNetCost(quote, shortlist.type) }}</span>
                    </div>
                </div>
                <div class="col-12 col-xl-6">
                    <div class="sub-actions position-relative">
                        <router-link
                            :to="{ name: 'Configure Vehicle', params: { shortListId: shortlist.shortListID, quoteId: quote.quoteID }}"
                            tag="button"
                            class="button button--independence-ghost button--small me-1">
                            <template v-if="quoteConfigured(quote)">
                                Reconfigure
                            </template>
                            <template v-else>Configure</template>
                        </router-link>
                        <div v-if="quoteConfigured(quote)" class="select-button">
                            <button v-if="quoteConfigured(quote)" class="button button--small ms-1"
                                    @click="selectItem(shortlist.type, quote)"
                                    :class="{ 'button--independence': quote.quoteID === selectedQuote.quoteID, 'button--independence-ghost': quote.quoteID !== selectedQuote.quoteID }">
                                <template v-if="quote.quoteID === selectedQuote.quoteID">
                                    Selected
                                </template>
                                <template v-else>Select</template>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

        </div>

    </div>

</template>

<script>
export default {
    name: "comparison-list-small",
    props:[
        "classes",
        "quote",
        "shortlist",
        "session",
        "expanded",
        "listIndex",
        "selectedQuote"
    ],
    data(){
        return{
            expandedStatus: null
        }
    },
    watch:{
        expandedStatus(value){
            this.$emit('update-expanded-status', value);
        }
    },
    methods:{
        selectItem(type, item) {
            this.$emit('select-quote', {
                quoteID: item.quoteID,
                quoteName: item.quote.definition.quoteName,
                type: type
            });
        },
        quoteConfigured(item) {
            // If paintwork or trim aren't set, the vehicle isn't configured yet.
            return !(item.quote.factoryOptionsGrouped['Paintwork'] === undefined ||
                item.quote.factoryOptionsGrouped['Trim'] === undefined);
        },

        getBikRate(shortlistItem){
            if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK1Rate)){
                return shortlistItem.quote.details.biK1Rate;
            }

            if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK2Rate)){
                return shortlistItem.quote.details.biK2Rate;
            }

            if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK3Rate)){
                return shortlistItem.quote.details.biK3Rate;
            }

            if (shortlistItem.quote && (this.session.driverProfile.incomeTaxBand.ratePercent == shortlistItem.quote.details.biK4Rate)){
                return shortlistItem.quote.details.biK4Rate;
            }
        },
        getBikValue(shortlistItem){
            if (shortlistItem.quote.taxCalculations){
                return shortlistItem.quote.taxCalculations.bikTaxAverage;
            }
        },
        getMonthlyVehiclePayment(shortlistItem){
            const rentalsPayment = Number(shortlistItem.quote.taxCalculations.employeeCostPrivateCar.motoringAndFinance_RentalsPayments);
            const maintenanceCost = Number(shortlistItem.quote.taxCalculations.employeeCostPrivateCar.motoringAndFinance_MaintenanceCost);
            return Number((rentalsPayment + maintenanceCost) / shortlistItem.term).toFixed(2);
        },

        getTotalCosts(shortListItem, type){
            let total = 0;
            let monthlyPayment = 0;
            let businessFuelCost = 0;
            let privateFuelCost = 0;
            let vehicleInsurance = 0;
            if (!shortListItem.quote.taxCalculations){ return false; }
            switch(type){
                case 'bch':
                    monthlyPayment = Number(this.getBikValue(shortListItem));
                    businessFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostCompanyCar.businessMileage_BusinessFuelCost) / shortListItem.term);
                    privateFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostCompanyCar.privateMileage_PrivateFuelCost) / shortListItem.term);
                    total =  monthlyPayment + businessFuelCost + privateFuelCost;
                    break;
                case 'pch':
                    monthlyPayment = Number(this.getMonthlyVehiclePayment(shortListItem));
                    businessFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostPrivateCar.businessMileage_BusinessFuelCost) / shortListItem.term);
                    privateFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostPrivateCar.privateMileage_PrivateFuelCost) / shortListItem.term);
                    vehicleInsurance = (Number(shortListItem.quote.taxCalculations.employeeCostPrivateCar.motoringAndFinance_InsuranceCost) / shortListItem.term);
                    total = monthlyPayment + businessFuelCost + privateFuelCost + vehicleInsurance;
                    break;
            }
            return Number(total).toFixed(2);
        },

        getCashAllowanceNet(shortlistItem){
            const cashAllowance = Number(this.getMonthlyCost(Math.abs(shortlistItem.quote.taxCalculations['employeeCostPrivateCar']['cashAllowanceReconciliation_CashAllowance']), shortlistItem));
            const cashIncomeTax = Number(this.getMonthlyCost(shortlistItem.quote.taxCalculations['employeeCostPrivateCar']['cashAllowanceReconciliation_IncomeTaxOnCashAlternative'], shortlistItem));
            const cash1NIC = Number(this.getMonthlyCost(shortlistItem.quote.taxCalculations['employeeCostPrivateCar']['cashAllowanceReconciliation_Class1NICOnCashAllowance'], shortlistItem));
            return Number(cashAllowance - (cashIncomeTax + cash1NIC)).toFixed(2);
        },

        getTotalNetCost(shortlistItem, type){
            switch(type) {
                case 'bch':
                    return this.getMonthlyCost(shortlistItem.quote.taxCalculations.employeeCostCompanyCar.totals_NetPresentValueNPV, shortlistItem);
                    break;
                case 'pch':
                    return this.getMonthlyCost(shortlistItem.quote.taxCalculations.employeeCostPrivateCar.totals_NetPresentValueNPV, shortlistItem);
                    break;
            }
        },

        getMonthlyCost(value, shortlistItem){
            return (Number(value) / shortlistItem.term).toFixed(2);
        },
        remove() {

        },
    }
}
</script>

<style scoped>

</style>
